/** Rating value shown beside stars — 0–5 inclusive, decimals allowed. */

export const RATING_DISPLAY_MIN = 0;
export const RATING_DISPLAY_MAX = 5;
export const RATING_DISPLAY_DEFAULT = 4.9;

/**
 * @param {unknown} raw
 * @returns {number}
 */
export function parseRatingDisplayValue(raw) {
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return clampRatingDisplayValue(raw);
  }
  const text = String(raw ?? "")
    .trim()
    .replace(",", ".");
  if (!text) return RATING_DISPLAY_DEFAULT;
  const parsed = Number.parseFloat(text);
  if (!Number.isFinite(parsed)) return RATING_DISPLAY_DEFAULT;
  return clampRatingDisplayValue(parsed);
}

/**
 * @param {number} value
 * @returns {number}
 */
export function clampRatingDisplayValue(value) {
  if (!Number.isFinite(value)) return RATING_DISPLAY_DEFAULT;
  return Math.min(
    RATING_DISPLAY_MAX,
    Math.max(RATING_DISPLAY_MIN, value)
  );
}

/**
 * @param {number} value
 * @returns {string}
 */
export function formatRatingDisplayValue(value) {
  const clamped = clampRatingDisplayValue(value);
  const fixed = clamped.toFixed(2).replace(/\.?0+$/, "");
  return fixed === "" ? "0" : fixed;
}

/**
 * Fill amount per star (0–1) for a 5-star row.
 *
 * @param {number} ratingValue clamped 0–5
 * @returns {number[]}
 */
export function resolveRatingStarFills(ratingValue) {
  const rating = clampRatingDisplayValue(ratingValue);
  return Array.from({ length: 5 }, (_, index) =>
    Math.min(1, Math.max(0, rating - index))
  );
}

/**
 * @param {object | null | undefined} section
 * @returns {object}
 */
export function normalizeRatingSectionDisplay(section) {
  const display = section?.setting?.display || {};
  const legacyWholeStars = Number(section?.rating);
  const fallback =
    Number.isFinite(legacyWholeStars) && legacyWholeStars >= 0
      ? legacyWholeStars
      : RATING_DISPLAY_DEFAULT;

  const numeric = parseRatingDisplayValue(
    display.ratingValue != null && String(display.ratingValue).trim() !== ""
      ? display.ratingValue
      : fallback
  );

  return {
    ...display,
    showStars: display.showStars !== false,
    ratingValue: formatRatingDisplayValue(numeric),
    reviewCount:
      typeof display.reviewCount === "number"
        ? display.reviewCount
        : Number.parseInt(String(display.reviewCount ?? ""), 10) || 0,
  };
}
